import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import Div from '@jarvis.oddle/core/components/Div'
import {
  borderRadius,
  borderRadiusSizes,
  colorConstants,
  margin,
  marginSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import Flex from '@jarvis.oddle/core/components/Flex'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import {
  InlineNotification,
  notificationStates
} from '@jarvis.oddle/core/components/InlineNotification'
import { H4, P } from '@jarvis.oddle/core/components/Typo'
import Divider from '@jarvis.oddle/core/components/Divider'
import { isObjectEmpty } from 'src/utils/object'
import { useAppContext } from 'src/containers/AppContext'
import { Section } from './Elements'

const OrderSummaryContainer = styled(Div).attrs(() => ({
  padding: paddingSizes.medium
}))`
  box-sizing: border-box;
  border-radius: ${borderRadius[borderRadiusSizes.medium]};
  min-height: 60px;
  background-color: ${colorConstants.lightestGray};
  box-shadow: none;
  border: 1px solid ${colorConstants.lightGray};
`

const RowContainer = styled(Flex).attrs(() => ({
  justifyContent: 'space-between'
}))`
  width: 100%;
  color: ${(props) => (props.color ? props.color : colorConstants.defaultText)};
  ${(props) => (props.normalised ? `margin: 0;` : '')}
`

const RowText = styled(P)`
  margin: 0;
`

const Row = ({
  leftText,
  rightText,
  normalised,
  bold,
  color,
  Text = RowText
}) => (
  <RowContainer normalised={normalised}>
    <Text color={color} bold={bold} paddingRight={paddingSizes.medium}>
      {leftText}
    </Text>
    <Text color={color} bold={bold} whiteSpace='nowrap'>
      {rightText}
    </Text>
  </RowContainer>
)

Row.propTypes = {
  leftText: PropTypes.string,
  rightText: PropTypes.string,
  normalised: PropTypes.bool,
  bold: PropTypes.bool,
  color: PropTypes.string,
  Text: PropTypes.oneOfType([PropTypes.func, PropTypes.object])
}

const RowHeader = styled(H4)`
  margin: ${margin[marginSizes.small]} 0;
`

const StyledSection = styled(Section)`
  &&& {
    padding-top: ${padding[paddingSizes.none]};
    padding-bottom: ${padding[paddingSizes.large]};
  }
`

interface TotalPayableSummaryProps {
  isFullyPaidByVoucher: boolean
  storeOrder: {
    data: Oms.V4.StoreOrder
  }
  voucherSummary: {
    voucherTitle?: string
    redeemedAmount?: number
  }
}

const TotalPayableSummary = ({
  storeOrder,
  voucherSummary = {},
  isFullyPaidByVoucher
}: TotalPayableSummaryProps): JSX.Element => {
  const { t } = React.useContext(TranslationContext)
  const { formatMoney } = useAppContext()

  const { data: storeOrderData } = storeOrder
  const { storeOrderPaymentDetails } = storeOrderData || {}
  const { paymentAmount } = storeOrderPaymentDetails || {}

  return (
    <StyledSection>
      <OrderSummaryContainer>
        <Row
          color={colorConstants.black}
          bold={
            isObjectEmpty(voucherSummary) ||
            isNaN(voucherSummary.redeemedAmount)
          }
          leftText={t('Total')}
          rightText={formatMoney(storeOrder.data.total)}
        />
        {!isObjectEmpty(voucherSummary) && voucherSummary.voucherTitle && (
          <React.Fragment>
            <Row
              color={colorConstants.black}
              leftText={t('Voucher | _voucherTitle_', {
                voucherTitle: voucherSummary.voucherTitle
              })}
              rightText={formatMoney(-1 * voucherSummary.redeemedAmount)}
            />
            <Divider color={colorConstants.gray} />
            <Row
              bold
              color={colorConstants.black}
              Text={RowHeader}
              leftText={t('Total Payable')}
              rightText={formatMoney(
                !isNaN(paymentAmount)
                  ? paymentAmount
                  : Math.max(
                      0,
                      storeOrder.data.total - voucherSummary.redeemedAmount
                    )
              )}
            />
          </React.Fragment>
        )}
      </OrderSummaryContainer>
      {isFullyPaidByVoucher && (
        <Div marginTop={marginSizes.medium}>
          <InlineNotification
            lightTheme
            text={t(
              'You have selected to use your Oddle Eats vouchers to pay for this order in full.'
            )}
            state={notificationStates.informative}
          />
        </Div>
      )}
    </StyledSection>
  )
}

export default TotalPayableSummary
